import { startMonitoringSession } from './monitoringSession';
import { isRecordingNow } from './activation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, usePaginatedQuery, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { coordinates, fencePolygons, inFences, type TrailPoint } from '../../../convex/lib/bodycamData';
export type Recording = {
  _id: string; externalId: string; deviceId: string; recordedAt: number; durationMs: number;
  title: string; guardName: string; address: string; latitude?: number; longitude?: number;
  locationTime?: number; expiresAt?: number; hasMedia: boolean; status: string;
};
export type BodycamPoint = { id: string; longitude: number; latitude: number; active: boolean; label: string };
export function useBodycams(enabled: boolean, sites: any[]) {
  const heartbeat = useMutation((api as any).bodycams.monitorHeartbeat);
  const [monitoring, setMonitoring] = useState(false);
  const overview = useQuery((api as any).bodycams.overview) as any;
  useEffect(() => {
    if (!overview?.configured || !overview?.autoSync) { setMonitoring(false); return; }
    const sessionId = crypto.randomUUID();
    return startMonitoringSession(remainingMs => heartbeat({ sessionId, activeUntil: Date.now() + remainingMs }), setMonitoring);
  }, [overview?.configured, overview?.autoSync, heartbeat]);
  const { results, status, loadMore } = usePaginatedQuery((api as any).bodycams.recordings, enabled ? {} : 'skip', { initialNumItems: 200 });
  const [availability, setAvailability] = useState<'all' | 'playable'>('playable');
  const [location, setLocation] = useState<'everywhere' | 'sites'>('everywhere');
  const [search, setSearch] = useState('');
  const [autoWatch, setAutoWatch] = useState(false);
  const autoWatchSeen = useRef(new Set<string>());
  const [focusRequest, setFocusRequest] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [trail, setTrail] = useState<TrailPoint[]>([]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = window.setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(timer); }, []);
  useEffect(() => { if (enabled && status === 'CanLoadMore') loadMore(300); }, [enabled, status, loadMore]);
  const polygons = useMemo(() => sites.filter(s => !s.isArchived).flatMap(s => fencePolygons(s.geoFenceCoordinates || s.GeoFenceCoordinatesJson)), [sites]);
  const records = results as Recording[];
  const spatialRecords = useMemo(() => location === 'everywhere' ? records : records.filter(row => {
    const c = coordinates(row.latitude, row.longitude);
    return c && inFences(c, polygons);
  }), [records, location, polygons]);
  const filtered = useMemo(() => spatialRecords.filter(row => {
    if (availability === 'playable' && (!row.hasMedia || !row.expiresAt || row.expiresAt <= now)) return false;
    return !search.trim() || `${row.guardName} ${row.deviceId} ${row.title} ${row.address}`.toLowerCase().includes(search.trim().toLowerCase());
  }), [spatialRecords, availability, now, search]);
  const selected = records.find(row => row._id === selectedId) || null;
  const select = useCallback((id: string | null) => { setSelectedId(id); setTrail([]); setFocusRequest(n => n + 1); }, []);
  const points = useMemo<BodycamPoint[]>(() => {
    const historical: BodycamPoint[] = enabled ? filtered.flatMap(r => {
      const c = coordinates(r.latitude, r.longitude);
      return c ? [{ id: r._id, longitude: c[0], latitude: c[1], active: false, label: r.guardName || r.deviceId }] : [];
    }) : [];
    return [...historical, ...(overview?.devices || []).flatMap((d: any) => {
      const c = coordinates(d.latitude, d.longitude);
      return c && isRecordingNow(d, now) && d.locationTime && now - d.locationTime < 120000 ? [{ id: `device:${d.deviceId}`, longitude: c[0], latitude: c[1], active: true, label: d.name }] : [];
    })];
  }, [enabled, filtered, overview?.devices, now]);
  return { overview, monitoring, autoWatch, setAutoWatch, autoWatchSeen, records, filtered, status, availability, setAvailability, location, setLocation, search, setSearch, selected, selectedId, focusRequest, select, points, trail, setTrail, now };
}
export type BodycamController = ReturnType<typeof useBodycams>;
